import React from "react";

const FabricCard = () => (
  <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0B1B3B] p-6 hover-lift" data-testid="tool-animation-fabric-card">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-md bg-[#1E56E3] text-white flex items-center justify-center text-[10px] font-extrabold">MS</span>
        <div>
          <div className="text-sm font-bold text-[#0B1B3B] dark:text-white">Microsoft Fabric</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-300">Unified data platform</div>
        </div>
      </div>
      <span className="h-2 w-2 rounded-full bg-[#1E56E3] pulse-dot" />
    </div>

    <svg viewBox="0 0 260 120" className="mt-5 w-full h-28">
      <g stroke="#BFD4FF" strokeWidth="2" fill="none" className="flow-line">
        <path d="M44 28 L 128 60" />
        <path d="M44 92 L 128 60" />
        <path d="M132 60 L 216 28" />
        <path d="M132 60 L 216 92" />
      </g>
      <g>
        <rect x="14" y="14" rx="12" width="60" height="28" fill="#EEF3FF" />
        <text x="44" y="32" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1E56E3">OneLake</text>
        <rect x="14" y="78" rx="12" width="60" height="28" fill="#EEF3FF" />
        <text x="44" y="96" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1E56E3">Pipelines</text>
        <rect x="96" y="42" rx="16" width="72" height="36" fill="#1E56E3" className="float-slow" />
        <text x="132" y="63" textAnchor="middle" fontSize="10" fontWeight="800" fill="#ffffff">Unified Hub</text>
        <rect x="186" y="14" rx="12" width="60" height="28" fill="#EEF3FF" />
        <text x="216" y="32" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1E56E3">Warehouse</text>
        <rect x="186" y="78" rx="12" width="60" height="28" fill="#EEF3FF" />
        <text x="216" y="96" textAnchor="middle" fontSize="9" fontWeight="700" fill="#1E56E3">Power BI</text>
      </g>
      <circle cx="128" cy="60" r="4" fill="#ffffff" className="pulse-dot" />
    </svg>

    <div className="mt-4 flex flex-col items-start gap-1 text-[11px] text-slate-500 dark:text-slate-300 sm:flex-row sm:items-center sm:justify-between">
      <span>Lakehouse · Warehouse · BI</span><span className="font-bold text-[#0B1B3B] dark:text-white">Single platform</span>
    </div>
  </div>
);

const databricksStages = [
  { label: "Bronze", title: "Ingest raw data", footer: "INGEST", bg: "from-[#FFE2E2] to-white", dot: "#F97316" },
  { label: "Silver", title: "Standardize & enrich", footer: "REFINE", bg: "from-[#FFD6D6] to-white", dot: "#EF4444" },
  { label: "Gold", title: "Analytics-ready data", footer: "SERVE", bg: "from-[#FFCACA] to-white", dot: "#DC2626" },
];

const databricksTransitions = ["Clean & validate", "Transform & aggregate"];

const StageCard = ({ stage }) => (
  <div className={`rounded-2xl border border-red-100 dark:border-red-500/20 bg-gradient-to-br ${stage.bg} dark:from-[#3A1A1E] dark:to-[#211012] px-3 py-4 min-h-[136px] flex flex-col justify-between shadow-sm`}>
    <div>
      <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#B91C1C] dark:text-[#FCA5A5]">{stage.label}</div>
      <div className="mt-2 text-[12px] leading-snug font-semibold text-[#7F1D1D] dark:text-white lg:text-[13px]">{stage.title}</div>
    </div>
    <div className="mt-5 inline-flex items-center gap-2 self-start rounded-full border border-white/40 bg-white/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#991B1B] dark:border-white/10 dark:bg-white/10 dark:text-[#FECACA]">
      <span className="h-2.5 w-2.5 rounded-full pulse-dot" style={{ background: stage.dot }} />
      <span>{stage.footer}</span>
    </div>
  </div>
);

const TransitionBlock = ({ label }) => (
  <div className="flex min-w-0 flex-col items-center justify-center gap-2 px-1 py-1 text-center">
    <span className="max-w-[68px] break-words text-[7px] font-black uppercase leading-tight tracking-[0.08em] text-[#B91C1C] dark:text-[#FECACA] lg:max-w-[80px] lg:text-[8px]">
      {label}
    </span>
    <div className="flex items-center justify-center gap-1 text-[#EF4444] dark:text-[#FCA5A5]" data-testid="databricks-transition-arrows">
      {[0, 1, 2].map((arrowIndex) => (
        <span key={`${label}-${arrowIndex}`} className="inline-block text-sm font-black leading-none databricks-arrow" style={{ animationDelay: `${arrowIndex * 0.18}s` }}>
          →
        </span>
      ))}
    </div>
  </div>
);

const DatabricksCard = () => (
  <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0B1B3B] p-6 hover-lift" data-testid="tool-animation-databricks-card">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-md bg-[#EF4444] text-white flex items-center justify-center text-[10px] font-extrabold">DB</span>
        <div>
          <div className="text-sm font-bold text-[#0B1B3B] dark:text-white">Databricks</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-300">Live medallion flow</div>
        </div>
      </div>
      <span className="h-2 w-2 rounded-full bg-[#EF4444] pulse-dot" />
    </div>

    <div className="mt-5 rounded-2xl bg-[#FFF5F5] dark:bg-[#2B1316] p-3 overflow-hidden">
      <div className="space-y-3 md:hidden">
        {databricksStages.map((stage, index) => (
          <React.Fragment key={stage.label}>
            <StageCard stage={stage} />
            {index < databricksTransitions.length && <TransitionBlock label={databricksTransitions[index]} />}
          </React.Fragment>
        ))}
      </div>

      <div className="hidden md:grid md:grid-cols-[minmax(96px,1fr)_46px_minmax(96px,1fr)_46px_minmax(96px,1fr)] md:items-stretch md:gap-2 lg:grid-cols-[minmax(108px,1fr)_56px_minmax(108px,1fr)_56px_minmax(108px,1fr)]">
        <StageCard stage={databricksStages[0]} />
        <TransitionBlock label={databricksTransitions[0]} />
        <StageCard stage={databricksStages[1]} />
        <TransitionBlock label={databricksTransitions[1]} />
        <StageCard stage={databricksStages[2]} />
      </div>

      <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#EF4444] font-bold">
        <span className="h-2 w-2 rounded-full bg-[#EF4444] pulse-dot" /> Streaming jobs in motion
      </div>
    </div>

    <div className="mt-4 flex flex-col items-start gap-1 text-[11px] text-slate-500 dark:text-slate-300 sm:flex-row sm:items-center sm:justify-between">
      <span>Delta pipelines</span><span className="font-bold text-[#0B1B3B] dark:text-white">Processing now</span>
    </div>
  </div>
);

const SnowflakeCard = () => (
  <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0B1B3B] p-6 hover-lift" data-testid="tool-animation-snowflake-card">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-md bg-[#38BDF8] text-white flex items-center justify-center text-[10px] font-extrabold">SF</span>
        <div>
          <div className="text-sm font-bold text-[#0B1B3B] dark:text-white">Snowflake</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-300">Elastic warehouse</div>
        </div>
      </div>
      <span className="h-2 w-2 rounded-full bg-[#38BDF8] pulse-dot" />
    </div>

    <div className="mt-5 space-y-3">
      {[
        { name: "XS Warehouse", bars: [30, 44, 52] },
        { name: "M Warehouse", bars: [72, 65, 80] },
        { name: "L Warehouse", bars: [48, 58, 40] },
      ].map((warehouse, index) => (
        <div key={warehouse.name} className="rounded-xl border border-sky-100 dark:border-sky-500/20 bg-[#F4FBFF] dark:bg-[#0F2548] px-4 py-3">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-300">
            <span>{warehouse.name}</span>
            <span className="font-bold text-[#0B1B3B] dark:text-white">Auto-scale</span>
          </div>
          <div className="mt-3 flex gap-2">
            {warehouse.bars.map((value, barIndex) => (
              <div key={`${warehouse.name}-${barIndex}`} className="h-2 flex-1 overflow-hidden rounded-full bg-white">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${value}%`,
                    background: "linear-gradient(90deg,#38BDF8,#0EA5E9)",
                    animation: `pulse 2s ease-in-out ${index * 0.25 + barIndex * 0.12}s infinite`,
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    <div className="mt-4 flex flex-col items-start gap-1 text-[11px] text-slate-500 dark:text-slate-300 sm:flex-row sm:items-center sm:justify-between">
      <span>Query lanes</span><span className="font-bold text-[#0B1B3B] dark:text-white">Warehouse load balanced</span>
    </div>
  </div>
);

const ADFCard = () => (
  <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0B1B3B] p-6 hover-lift" data-testid="tool-animation-adf-card">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-md bg-[#0078D4] text-white flex items-center justify-center text-[9px] font-extrabold">ADF</span>
        <div>
          <div className="text-sm font-bold text-[#0B1B3B] dark:text-white">Azure Data Factory</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-300">Data orchestration</div>
        </div>
      </div>
      <span className="h-2 w-2 rounded-full bg-[#0078D4] pulse-dot" />
    </div>

    <svg viewBox="0 0 260 110" className="mt-5 w-full h-24">
      <g stroke="#7CA6FF" strokeWidth="2" fill="none" className="flow-line">
        <path d="M36 24 L 112 24 L 112 54 L 188 54" />
        <path d="M36 86 L 112 86 L 112 54 L 188 54" />
        <path d="M188 54 L 228 54" />
      </g>
      {[
        { x: 12, y: 10, label: "Source A" },
        { x: 12, y: 72, label: "Source B" },
        { x: 88, y: 40, label: "Transform" },
        { x: 164, y: 40, label: "Route" },
        { x: 210, y: 40, label: "Load" },
      ].map((node, index) => (
        <g key={node.label} style={{ animation: `float 3s ease-in-out ${index * 0.2}s infinite` }}>
          <rect x={node.x} y={node.y} rx="12" width="40" height="28" fill={index < 2 ? "#EEF3FF" : "#0078D4"} />
          <text x={node.x + 20} y={node.y + 17} textAnchor="middle" fontSize="8" fontWeight="700" fill={index < 2 ? "#1E56E3" : "#ffffff"}>{node.label}</text>
        </g>
      ))}
      <circle cx="112" cy="54" r="4" fill="#0078D4" className="pulse-dot" />
    </svg>

    <div className="mt-4 flex flex-col items-start gap-1 text-[11px] text-slate-500 dark:text-slate-300 sm:flex-row sm:items-center sm:justify-between">
      <span>Pipeline branches</span><span className="font-bold text-[#0B1B3B] dark:text-white">Metadata-driven</span>
    </div>
  </div>
);

const DbtCard = () => (
  <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0B1B3B] p-6 hover-lift" data-testid="tool-animation-dbt-card">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-md bg-[#FF694A] text-white flex items-center justify-center text-[10px] font-extrabold">dbt</span>
        <div>
          <div className="text-sm font-bold text-[#0B1B3B] dark:text-white">dbt</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-300">SQL transform</div>
        </div>
      </div>
      <span className="h-2 w-2 rounded-full bg-[#FF694A] pulse-dot" />
    </div>
    <svg viewBox="0 0 240 72" className="mt-5 w-full h-16">
      <g>
        <circle cx="30" cy="20" r="6" fill="#FF694A" className="pulse-dot" />
        <circle cx="30" cy="52" r="6" fill="#FF694A" className="pulse-dot" />
        <circle cx="110" cy="36" r="7" fill="#FF694A" />
        <circle cx="180" cy="20" r="6" fill="#0B1B3B" />
        <circle cx="180" cy="52" r="6" fill="#0B1B3B" />
        <circle cx="225" cy="36" r="7" fill="#0B1B3B" />
      </g>
      <g stroke="#FF694A" strokeWidth="1.6" fill="none" strokeDasharray="5 4" className="flow-line">
        <path d="M30 20 L 105 32" />
        <path d="M30 52 L 105 40" />
        <path d="M115 30 L 175 20" />
        <path d="M115 42 L 175 52" />
        <path d="M185 20 L 220 34" />
        <path d="M185 52 L 220 38" />
      </g>
    </svg>
    <div className="mt-4 flex flex-col items-start gap-1 text-[11px] text-slate-500 dark:text-slate-300 sm:flex-row sm:items-center sm:justify-between">
      <span>Tests passing</span><span className="font-bold text-[#0B1B3B] dark:text-white">312 / 312</span>
    </div>
  </div>
);

const PowerBICard = () => (
  <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0B1B3B] p-6 hover-lift" data-testid="tool-animation-powerbi-card">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-md bg-[#F2C811] text-[#0B1B3B] flex items-center justify-center text-[9px] font-extrabold">PBI</span>
        <div>
          <div className="text-sm font-bold text-[#0B1B3B] dark:text-white">Power BI</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-300">Dashboard live</div>
        </div>
      </div>
      <span className="h-2 w-2 rounded-full bg-[#F59E0B] pulse-dot" />
    </div>

    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div className="rounded-2xl bg-[#FFF9DB] dark:bg-[#3C2E08] p-4 sm:col-span-2">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-300">Revenue trend</div>
        <svg viewBox="0 0 140 56" className="mt-3 w-full h-14">
          <path d="M0 40 C 20 30, 28 20, 46 26 S 74 44, 92 22 S 124 18, 140 10" stroke="#D97706" strokeWidth="3" fill="none" className="flow-line" />
          {[0, 46, 92, 140].map((x) => <circle key={x} cx={x} cy={x === 0 ? 40 : x === 46 ? 26 : x === 92 ? 22 : 10} r="3.5" fill="#F59E0B" className="pulse-dot" />)}
        </svg>
      </div>
      <div className="rounded-2xl bg-[#FFF9DB] dark:bg-[#3C2E08] p-4 flex flex-col justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-300">SLA</div>
        <div className="text-2xl font-extrabold text-[#0B1B3B] dark:text-white">99%</div>
        <div className="text-[10px] font-semibold text-[#D97706]">Live tile</div>
      </div>
    </div>

    <div className="mt-4 flex flex-col items-start gap-1 text-[11px] text-slate-500 dark:text-slate-300 sm:flex-row sm:items-center sm:justify-between">
      <span>Direct Lake tiles</span><span className="font-bold text-[#0B1B3B] dark:text-white">Realtime visual refresh</span>
    </div>
  </div>
);

const ToolAnimations = () => {
  return (
    <section className="py-24 bg-white dark:bg-[#08132C]">
      <div className="container-x">
        <div className="max-w-3xl reveal">
          <span className="tag-chip">Live · Per-Tool</span>
          <h2 className="section-title mt-4">Every platform, in motion</h2>
          <p className="section-sub mt-5">
            Distinct live animations for the exact platforms V4 Data And AI Solutions engineers every day — from Databricks processing and Snowflake warehouses to ADF orchestration, Power BI dashboards, and Microsoft Fabric unification.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <FabricCard />
          <div className="md:col-span-2 lg:col-span-2">
            <DatabricksCard />
          </div>
          <SnowflakeCard />
          <ADFCard />
          <DbtCard />
          <PowerBICard />
        </div>
      </div>
    </section>
  );
};

export default ToolAnimations;